import classnames from 'classnames';
import type * as React from 'react';
import { createPortal } from 'react-dom';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import './Tooltip.css';

interface TooltipProps {
  /**
   * Content to display in the tooltip
   */
  title: string;
  /**
   * Content that triggers the tooltip
   */
  children: React.ReactNode;
  /**
   * Additional class name applied to the trigger element
   */
  className?: string;
  /**
   * Renders the tooltip as an error tooltip (red message text). The body is
   * portaled to document.body, so trigger classes can't reach it — the caller
   * must flag error tooltips here since Tooltip only sees the title string.
   */
  isError?: boolean;
  /**
   * Placement of the tooltip
   */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Additional class name when tooltip is active
   */
  activeClassName?: string;
  /**
   * Optional ID for the tooltip
   */
  id?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
  /**
   * Whether the child provides its own interactive semantics.
   * @default false
   */
  hasInteractiveChild?: boolean;
}

function Tooltip({
  title,
  children,
  className,
  isError,
  placement = 'top',
  activeClassName,
  id,
  'data-testid': dataTestId,
  hasInteractiveChild = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ left: 0, top: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const baseTestId = dataTestId ?? 'tooltip';

  const tooltipClasses = classnames('access-tooltip', className, {
    'access-tooltip--active': isVisible,
    [activeClassName || '']: isVisible && activeClassName,
  });

  const tooltipBodyClasses = classnames('access-tooltip__body', `access-tooltip__body--${placement}`, {
    'access-tooltip__body--error': isError,
    'access-tooltip__body--visible': isVisible,
  });

  const computePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 10;

    switch (placement) {
      case 'top':
        setPosition({
          left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
          top: triggerRect.top - tooltipRect.height - gap,
        });
        break;
      case 'bottom':
        setPosition({
          left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
          top: triggerRect.bottom + gap,
        });
        break;
      case 'left':
        setPosition({
          left: triggerRect.left - tooltipRect.width - gap,
          top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
        });
        break;
      case 'right':
        setPosition({
          left: triggerRect.right + gap,
          top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
        });
        break;
    }
  }, [placement]);

  useLayoutEffect(() => {
    if (!isVisible) return;
    computePosition();
  }, [isVisible, computePosition]);

  useLayoutEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => {
      setIsVisible(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isVisible]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible((prev) => !prev);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsVisible((prev) => !prev);
    } else if (e.key === 'Escape') {
      setIsVisible(false);
    }
  };

  const tooltipBody = isVisible && (
    <span
      ref={tooltipRef}
      className={tooltipBodyClasses}
      data-testid={`${baseTestId}-content`}
      id={id ? `${id}-content` : undefined}
      role='tooltip'
      style={{
        left: `${position.left}px`,
        position: 'fixed',
        top: `${position.top}px`,
      }}>
      {title}
    </span>
  );

  return (
    <span
      ref={triggerRef}
      aria-describedby={!hasInteractiveChild && isVisible && id ? `${id}-content` : undefined}
      aria-label={hasInteractiveChild ? undefined : title}
      className={tooltipClasses}
      data-testid={baseTestId}
      id={id}
      role={hasInteractiveChild ? undefined : 'button'}
      tabIndex={hasInteractiveChild ? undefined : 0}
      onBlur={() => setIsVisible(false)}
      onClick={hasInteractiveChild ? undefined : handleClick}
      onFocus={() => setIsVisible(true)}
      onKeyDown={hasInteractiveChild ? undefined : handleKeyDown}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}>
      <span
        className='access-tooltip__trigger'
        data-testid={`${baseTestId}-trigger`}>
        {children}
      </span>
      {tooltipBody && createPortal(tooltipBody, document.body)}
    </span>
  );
}

export default Tooltip;
